import { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import { db } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { confirmDestructive } from "../../lib/confirm";
import type { AeropuertoDoc } from "../../lib/types";

interface AeropuertoRow extends AeropuertoDoc {
  id: string;
}

type SortKey = "icao" | "nombre" | "ciudad" | "uso" | "activo" | "provisional" | "coord";

function compareRows(a: AeropuertoRow, b: AeropuertoRow, key: SortKey, usage: Map<string, number>): number {
  const str = (x: string | null | undefined) => (x ?? "").toLowerCase();
  switch (key) {
    case "icao": return str(a.id.replace(/^_/, "")).localeCompare(str(b.id.replace(/^_/, "")));
    case "nombre": return str(a.displayName).localeCompare(str(b.displayName));
    case "ciudad": return str(a.city).localeCompare(str(b.city)) || str(a.displayName).localeCompare(str(b.displayName));
    case "uso": return (usage.get(a.id) ?? 0) - (usage.get(b.id) ?? 0) || str(a.displayName).localeCompare(str(b.displayName));
    case "activo": return Number(b.active) - Number(a.active) || str(a.displayName).localeCompare(str(b.displayName));
    case "provisional": return Number(b.isProvisional) - Number(a.isProvisional) || str(a.displayName).localeCompare(str(b.displayName));
    case "coord": return Number(a.lat != null && a.lon != null) - Number(b.lat != null && b.lon != null) || str(a.displayName).localeCompare(str(b.displayName));
  }
}

/**
 * /admin/locations equivalent: list + edit Aeropuertos. ICAO is the
 * natural doc id (4-char, sigil-padded `_XXX` for 3-char codes — same
 * as v1). Provisional rows highlight in amber so admin can curate.
 */
export function AdminAeropuertos() {
  const [rows, setRows] = useState<AeropuertoRow[] | null>(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<AeropuertoRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "nombre", dir: 1 });

  useEffect(() => {
    const q = query(collection(db, "Aeropuertos"), orderBy("displayCombined"));
    const unsub = onSnapshot(q, (snap) => {
      const list: AeropuertoRow[] = [];
      snap.forEach((d) =>
        list.push({ id: d.id, ...(d.data() as AeropuertoDoc) })
      );
      setRows(list);
    });
    return () => unsub();
  }, []);

  // Usage counts: how many Vuelos reference each icao as origin or
  // destination. Loaded once on mount — admins curating airports want
  // to know what they'd break by editing/deleting. Excludes cancelled
  // + rejected because those don't reflect "active" usage.
  const [usage, setUsage] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "Vuelos"));
        const counts = new Map<string, number>();
        snap.forEach((d) => {
          const f = d.data() as {
            originIcao?: string;
            destinationIcao?: string;
            status?: string;
          };
          if (f.status === "cancelled" || f.status === "rejected") return;
          if (f.originIcao) {
            counts.set(f.originIcao, (counts.get(f.originIcao) ?? 0) + 1);
          }
          if (f.destinationIcao) {
            counts.set(
              f.destinationIcao,
              (counts.get(f.destinationIcao) ?? 0) + 1
            );
          }
        });
        setUsage(counts);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[aeropuertos] usage count failed:", err);
      }
    })();
  }, []);

  if (rows === null) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  const term = filter.trim().toLowerCase();
  const filtered = term
    ? rows.filter((r) => {
        const haystack = [
          r.id,
          r.id.replace(/^_/, ""),
          r.displayName,
          r.city ?? "",
          r.displayCombined,
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      })
    : rows;

  const sorted = [...filtered].sort((a, b) => compareRows(a, b, sort.key, usage) * sort.dir);
  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }
  const Th = ({ k, children, center }: { k: SortKey; children: React.ReactNode; center?: boolean }) => (
    <th
      onClick={() => toggleSort(k)}
      className={"cursor-pointer select-none px-3 py-2 hover:text-slate-800 " + (center ? "text-center" : "")}
      title="Ordenar por esta columna"
    >
      {children}
      {sort.key === k ? <span className="ml-1">{sort.dir === 1 ? "▲" : "▼"}</span> : null}
    </th>
  );

  const provisionalCount = rows.filter((r) => r.isProvisional).length;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Aeropuertos</h1>
          <p className="mt-1 text-sm text-slate-600">
            {rows.length} aeropuertos
            {provisionalCount > 0 ? (
              <>
                {" · "}
                <strong className="text-amber-700">
                  {provisionalCount} provisional{provisionalCount === 1 ? "" : "es"}
                </strong>
                {" "}por revisar
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RefreshDisplaysButton />
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
          >
            + Nuevo aeropuerto
          </button>
        </div>
      </header>

      <input
        type="text"
        placeholder="Buscar por ICAO o nombre…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="block w-full max-w-md rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
      />

      {/* Mobile cards. Desktop table renders on md+. */}
      <ul className="space-y-2 md:hidden">
        {filtered.length === 0 ? (
          <li className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
            Sin resultados.
          </li>
        ) : (
          filtered.map((r) => {
            const code = r.id.replace(/^_/, "");
            return (
              <li
                key={r.id}
                onClick={() => setEditing(r)}
                className={
                  "cursor-pointer rounded-lg border p-3 text-sm " +
                  (r.isProvisional
                    ? "border-amber-300 bg-amber-50"
                    : "border-slate-200 bg-white")
                }
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-900">
                      {r.displayName}
                    </div>
                    <div className="font-mono text-xs text-slate-600">
                      {code}
                      {r.city ? (
                        <span className="ml-2 text-slate-500">{r.city}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    {r.active ? (
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">
                        activo
                      </span>
                    ) : (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">
                        inactivo
                      </span>
                    )}
                    {r.isProvisional ? (
                      <span className="rounded bg-amber-200 px-1.5 py-0.5 text-amber-900">
                        provisional
                      </span>
                    ) : null}
                    {r.lat == null || r.lon == null ? (
                      <span className="rounded bg-rose-100 px-1.5 py-0.5 text-rose-800" title="Sin latitud/longitud">
                        sin coord.
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2 text-right text-xs">
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (
                        confirmDestructive(
                          `¿Borrar aeropuerto ${code} (${r.displayName})?\n\n` +
                            `Si algún vuelo o distancia lo referencia, esa fila quedará rota.`
                        )
                      ) {
                        try {
                          await deleteDoc(doc(db, "Aeropuertos", r.id));
                        } catch (err) {
                          alert(
                            `No se pudo borrar: ${err instanceof Error ? err.message : String(err)}`
                          );
                        }
                      }
                    }}
                    className="text-rose-700 hover:text-rose-900"
                  >
                    Borrar
                  </button>
                </div>
              </li>
            );
          })
        )}
      </ul>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <Th k="icao">ICAO</Th>
              <Th k="nombre">Nombre</Th>
              <Th k="ciudad">Ciudad</Th>
              <th className="px-3 py-2">Display</th>
              <Th k="uso" center>Uso</Th>
              <Th k="activo" center>Activo</Th>
              <Th k="provisional" center>Provisional</Th>
              <Th k="coord" center>Coord.</Th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((r) => (
              <tr
                key={r.id}
                onClick={() => setEditing(r)}
                className={
                  "cursor-pointer " +
                  (r.isProvisional
                    ? "bg-amber-50 hover:bg-amber-100"
                    : "hover:bg-sky-50")
                }
              >
                <td className="px-3 py-2 font-mono text-xs text-slate-700">
                  {r.id.replace(/^_/, "")}
                </td>
                <td className="px-3 py-2 text-slate-900">{r.displayName}</td>
                <td className="px-3 py-2 text-slate-600">{r.city ?? "—"}</td>
                <td className="px-3 py-2 text-slate-600">{r.displayCombined}</td>
                <td className="px-3 py-2 text-center text-xs font-mono text-slate-700">
                  {usage.get(r.id) ?? 0}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.active ? "✓" : "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.isProvisional ? "⚠️" : "—"}
                </td>
                <td
                  className="px-3 py-2 text-center"
                  title={r.lat != null && r.lon != null ? `${r.lat}, ${r.lon}` : "Sin coordenadas"}
                >
                  {r.lat != null && r.lon != null ? "✓" : <span className="font-medium text-rose-600">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const code = r.id.replace(/^_/, "");
                      if (
                        confirmDestructive(
                          `¿Borrar aeropuerto ${code} (${r.displayName})?\n\n` +
                            `Si algún vuelo o distancia lo referencia, esa fila quedará rota.`
                        )
                      ) {
                        try {
                          await deleteDoc(doc(db, "Aeropuertos", r.id));
                        } catch (err) {
                          alert(
                            `No se pudo borrar: ${err instanceof Error ? err.message : String(err)}`
                          );
                        }
                      }
                    }}
                    className="text-xs text-rose-700 hover:text-rose-900"
                  >
                    Borrar
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-sm text-slate-500"
                >
                  Sin resultados.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {editing ? (
        <AeropuertoEditor
          initial={editing}
          usageCount={usage.get(editing.id) ?? 0}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {creating ? (
        <AeropuertoEditor initial={null} onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

/**
 * One-shot maintenance: rewrites Aeropuertos.displayCombined to the
 * canonical "displayName (icao)" form for every doc. Useful after
 * dropping city from the label — earlier rows had it baked in.
 */
function RefreshDisplaysButton() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function run() {
    if (!window.confirm("¿Reconstruir el display de todos los aeropuertos sin ciudad?")) {
      return;
    }
    setBusy(true);
    setDone(null);
    try {
      const snap = await getDocs(collection(db, "Aeropuertos"));
      let batch = writeBatch(db);
      let count = 0;
      let pending = 0;
      for (const d of snap.docs) {
        const data = d.data() as AeropuertoDoc;
        const shown = d.id.replace(/^_/, "");
        const next = `${data.displayName} (${shown})`;
        if (next !== data.displayCombined) {
          batch.update(d.ref, { displayCombined: next });
          count++;
          pending++;
          if (pending >= 400) {
            await batch.commit();
            batch = writeBatch(db);
            pending = 0;
          }
        }
      }
      if (pending > 0) await batch.commit();
      setDone(`Listo · ${count} aeropuertos actualizados.`);
    } catch (err) {
      setDone(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <SavingOverlay open={busy} label="Reconstruyendo…" />
      <button
        type="button"
        disabled={busy}
        onClick={run}
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
        title="Rewrite displayCombined for all airports to drop the city"
      >
        {busy ? "Reconstruyendo…" : "Refrescar displays"}
      </button>
      {done ? <span className="text-xs text-slate-600">{done}</span> : null}
    </div>
  );
}

function AeropuertoEditor({
  initial,
  usageCount,
  onClose,
}: {
  initial: AeropuertoRow | null;
  usageCount?: number;
  onClose: () => void;
}) {
  const isNew = initial === null;
  const [icaoRaw, setIcaoRaw] = useState(
    initial ? initial.id.replace(/^_/, "") : ""
  );
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [fplContacto, setFplContacto] = useState(initial?.fplContacto ?? "");
  const [latRaw, setLatRaw] = useState(initial?.lat != null ? String(initial.lat) : "");
  const [lonRaw, setLonRaw] = useState(initial?.lon != null ? String(initial.lon) : "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [isProvisional, setIsProvisional] = useState(
    initial?.isProvisional ?? false
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function padToFour(raw: string): string | null {
    const c = raw.toUpperCase().trim();
    if (/^[A-Z0-9]{4}$/.test(c)) return c;
    if (/^[A-Z0-9]{3}$/.test(c)) return `_${c}`;
    return null;
  }

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const docId = padToFour(icaoRaw);
      if (!docId) {
        throw new Error("ICAO debe ser 3 o 4 caracteres alfanuméricos.");
      }
      const display = displayName.trim();
      if (!display) throw new Error("Nombre obligatorio.");
      const shown = docId.replace(/^_/, "");
      const cityValue = city.trim() || null;
      // Coordenadas en decimales (negativo = sur / oeste). Acepta coma decimal.
      const parseCoord = (raw: string, name: string, max: number): number | null => {
        const t = raw.trim().replace(",", ".");
        if (!t) return null;
        const v = Number(t);
        if (!Number.isFinite(v) || Math.abs(v) > max) {
          throw new Error(`${name} inválida: usa decimales, ej. -33.382806 (negativo para sur/oeste).`);
        }
        return v;
      };
      const lat = parseCoord(latRaw, "Latitud", 90);
      const lon = parseCoord(lonRaw, "Longitud", 180);
      if ((lat == null) !== (lon == null)) {
        throw new Error("Latitud y longitud van juntas: completa ambas o deja ambas vacías.");
      }
      // displayCombined never includes the city — that's column data,
      // not label data. The combobox includes city in its search
      // haystack so typing "santiago" still surfaces "Pudahuel (SCEL)".
      const combined = `${display} (${shown})`;
      const data = {
        displayName: display,
        city: cityValue,
        displayCombined: combined,
        active,
        isProvisional,
        fplContacto: fplContacto.trim() || null,
        lat,
        lon,
      };
      const ref = doc(db, "Aeropuertos", docId);
      if (isNew) {
        await setDoc(ref, {
          ...data,
          createdAt: serverTimestamp(),
        });
      } else {
        await updateDoc(ref, data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          {isNew ? "Nuevo aeropuerto" : `Editar ${initial!.id.replace(/^_/, "")}`}
        </h2>
        {!isNew && usageCount && usageCount > 0 ? (
          <p className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            ⚠️ Usado en <strong>{usageCount}</strong> vuelo
            {usageCount === 1 ? "" : "s"} activo
            {usageCount === 1 ? "" : "s"}. Cambiar el ICAO romperá esas
            referencias. Si solo arreglas nombre / ciudad / lat-lon, no
            hay problema.
          </p>
        ) : null}
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              ICAO
            </label>
            <input
              type="text"
              value={icaoRaw}
              onChange={(e) => setIcaoRaw(e.target.value.toUpperCase())}
              disabled={!isNew}
              maxLength={4}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Nombre del aeropuerto
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Ciudad
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={120}
              placeholder="Santiago"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Latitud</label>
              <input
                type="text"
                inputMode="decimal"
                value={latRaw}
                onChange={(e) => setLatRaw(e.target.value)}
                placeholder="-33.382806"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Longitud</label>
              <input
                type="text"
                inputMode="decimal"
                value={lonRaw}
                onChange={(e) => setLonRaw(e.target.value)}
                placeholder="-70.567083"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <p className="col-span-2 -mt-1 text-xs text-slate-500">
              Decimales, negativo para sur y oeste. Se usan para el filtro de ruta de NOTAMs, las referencias de ubicación y el clima; sin coordenadas el vuelo se analiza solo por aeródromos.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Contacto plan de vuelo
            </label>
            <input
              type="text"
              value={fplContacto}
              onChange={(e) => setFplContacto(e.target.value)}
              maxLength={80}
              placeholder="Santiago en 12240"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
            <p className="mt-1 text-xs text-slate-500">
              Solo aeródromos sin torre: con quién se activa o cancela el plan de vuelo. Va al texto para ForeFlight ("Activo con … y cancelo con …"). Vacío si tiene torre.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-slate-300"
            />
            Activo
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isProvisional}
              onChange={(e) => setIsProvisional(e.target.checked)}
              className="rounded border-slate-300"
            />
            Provisional (creado desde texto libre — pendiente de revisión)
          </label>
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
